import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    TextField,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCustomerLoyaltyProgram } from "../../shared/hooks/useLoyaltyData";
import { PosContext } from "../../shared/PosContext";

const StatBlock = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
            {value}
        </Typography>
    </Box>
);

const LoyaltyProgramDrawer = ({ open, onClose, customer, company }) => {
    const { t } = useTranslation();
    const { customers } = useContext(PosContext);

    // The drawer can be opened from a global action without a pre-selected
    // customer, so it keeps its own selection (seeded from the prop) to stay
    // functional on its own.
    const [localCustomer, setLocalCustomer] = useState(customer || null);

    useEffect(() => {
        if (open) {
            setLocalCustomer(customer || null);
        }
    }, [open, customer]);

    const effectiveCustomer = localCustomer || null;

    const loyaltyQuery = useCustomerLoyaltyProgram(effectiveCustomer, company, {
        enabled: !!open,
    });
    const loyalty = loyaltyQuery.data;
    const isBusy = loyaltyQuery.isLoading || loyaltyQuery.isFetching;

    const customerOptions = useMemo(
        () =>
            (customers || []).map((c) => ({
                value: c.name,
                label: c.customer_name || c.name,
            })),
        [customers]
    );

    const selectedOption = useMemo(() => {
        if (!effectiveCustomer) return null;
        return (
            customerOptions.find((o) => o.value === effectiveCustomer) || {
                value: effectiveCustomer,
                label: effectiveCustomer,
            }
        );
    }, [customerOptions, effectiveCustomer]);

    return (
        <Drawer anchor="right" open={!!open} onClose={onClose}>
            <Box sx={{ width: 420, p: 2 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                    gap={1}
                >
                    <Typography variant="h6">{t("loyalty.drawerTitle")}</Typography>
                    <Button
                        variant="outlined"
                        onClick={() => loyaltyQuery.refetch?.()}
                        disabled={!effectiveCustomer || isBusy}
                    >
                        {t("common.refresh")}
                    </Button>
                </Box>

                <Autocomplete
                    options={customerOptions}
                    value={selectedOption}
                    onChange={(_, option) =>
                        setLocalCustomer(option?.value || null)
                    }
                    isOptionEqualToValue={(o, v) => o.value === v?.value}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label={t("loyalty.customerPickerLabel")}
                            placeholder={t("loyalty.customerPickerPlaceholder")}
                        />
                    )}
                    sx={{ mb: 2 }}
                />

                {!effectiveCustomer && (
                    <Typography variant="body2" color="text.secondary">
                        {t("loyalty.selectCustomer")}
                    </Typography>
                )}

                {effectiveCustomer && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t("loyalty.drawerSubtitle", { customer: effectiveCustomer })}
                    </Typography>
                )}

                {effectiveCustomer && isBusy && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={3}
                        gap={1}
                    >
                        <CircularProgress size={20} />
                        <Typography variant="body2">
                            {t("common.loading")}
                        </Typography>
                    </Box>
                )}

                {effectiveCustomer && !isBusy && !loyalty?.hasProgram && (
                    <Typography variant="body2" color="text.secondary">
                        {t("loyalty.noProgram")}
                    </Typography>
                )}

                {effectiveCustomer && loyalty?.hasProgram && !isBusy && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {loyalty.loyaltyProgramName || loyalty.loyaltyProgram}
                            </Typography>
                            {loyalty.tierName && (
                                <Typography variant="body2" color="text.secondary">
                                    {t("loyalty.tierName", {
                                        tier: loyalty.tierName,
                                    })}
                                </Typography>
                            )}
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(150px, 1fr))",
                                gap: 1.5,
                                mb: 2,
                            }}
                        >
                            <StatBlock
                                label={t("loyalty.availablePoints")}
                                value={loyalty.loyaltyPoints}
                            />
                            <StatBlock
                                label={t("loyalty.totalSpent")}
                                value={`₪${Number(loyalty.totalSpent || 0).toFixed(2)}`}
                            />
                            <StatBlock
                                label={t("loyalty.conversionFactor")}
                                value={`₪${Number(loyalty.conversionFactor || 0).toFixed(2)}`}
                            />
                            <StatBlock
                                label={t("loyalty.redemptionValue")}
                                value={`₪${Number(loyalty.redemptionValue || 0).toFixed(2)}`}
                            />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: "grid", gap: 1.25 }}>
                            <StatBlock
                                label={t("loyalty.minimumRedemption")}
                                value={loyalty.minimumRedemption || t("loyalty.notSet")}
                            />
                            <StatBlock
                                label={t("loyalty.maximumRedemption")}
                                value={loyalty.maximumRedemption || t("loyalty.notSet")}
                            />
                            <StatBlock
                                label={t("loyalty.dailyUsage")}
                                value={
                                    loyalty.maxDailyRedemptions > 0
                                        ? t("loyalty.dailyUsageValue", {
                                              used: loyalty.timesRedeemedToday,
                                              total: loyalty.maxDailyRedemptions,
                                          })
                                        : t("loyalty.unlimitedDailyUsage", {
                                              used: loyalty.timesRedeemedToday,
                                          })
                                }
                            />
                            <StatBlock
                                label={t("loyalty.status")}
                                value={
                                    loyalty.canRedeem
                                        ? t("loyalty.readyToRedeem")
                                        : loyalty.disabledReason ||
                                          t("loyalty.unavailable")
                                }
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Drawer>
    );
};

export default LoyaltyProgramDrawer;
